import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, FormInstance, Image, Message, Modal, Progress, Select, Upload } from '@arco-design/web-react';
import { IconDelete, IconPlus } from '@arco-design/web-react/icon';

import { useSvgImageUrls } from '@/hooks/useSvgImageUrls';
import { uploadImg, uploadImgGlobal } from '@/utils/s3-upload';

import styles from './index.module.less';

export type ImageKeyScope = 'product' | 'product-type' | 'carousel';
/** 凭证作用域：系统类别图用全局；租户充值凭证等仍走数据中心 */
export type ImageTokenScope = 'dc' | 'global';

/** 组装 S3 object key：产品 `{id}/{name}`；类别 `product-type/{id}/{name}`；轮播 `carousel/{id}/{name}` */
function buildImageKey(scope: ImageKeyScope, id: string, fileName: string) {
  if (scope === 'product-type') return `product-type/${id}/${fileName}`;
  if (scope === 'carousel') return `carousel/${id}/${fileName}`;
  return `${id}/${fileName}`;
}

export function ImageUploadItem({
  form,
  data,
  showName = true,
  save = false,
  width = 80,
  gap = 10,
  maxCount,
  onUploadSuccess,
  fieldName = 'imageNames',
  disabled = false,
  /** S3 key 作用域，默认产品路径；产品类别用 product-type；轮播用 carousel */
  keyScope = 'product',
  /** STS 前缀：默认全局；充值凭证等传 dc */
  tokenScope = 'global' as ImageTokenScope,
  /** 自动保存时调用的 PATCH（如类别 images） */
  onSaveImages,
  /** 保存成功后回调（用于刷新详情） */
  onSaved,
  /** 整行预览（首页轮播图场景） */
  fullWidth = false,
  /** 预览区宽高比，例如 2560/853 */
  aspectRatio,
  /** 上传区下方说明 */
  hint,
  /** 覆盖默认 accept */
  accept = 'image/*',
  /**
   * 上传前校验/转换：返回 false 中止；返回 File 则用转换后的文件上传（如轮播压缩）。
   */
  beforeUploadValidate,
}: {
  form?: FormInstance | undefined;
  /** 列表创建清空选中项时可能短暂为 undefined，effect 内需空值安全 */
  data?: {
    id: string;
    imageUrls?: string[];
    imageNames?: string[];
    [key: string]: any;
  } | null;
  showName?: boolean;
  save?: boolean;
  width?: number;
  gap?: number;
  maxCount?: number;
  onUploadSuccess?: (file: any) => void;
  fieldName?: string;
  disabled?: boolean;
  keyScope?: ImageKeyScope;
  tokenScope?: ImageTokenScope;
  onSaveImages?: (id: string, imageNames: string[]) => Promise<any>;
  onSaved?: () => void;
  fullWidth?: boolean;
  aspectRatio?: number;
  hint?: ReactNode;
  accept?: string;
  beforeUploadValidate?: (file: File) => boolean | File | Promise<boolean | File>;
}) {
  const { t } = useTranslation();
  const [fileList, setFileList] = useState<any>([]);
  const [imageNames, setImageNames] = useState<string[]>([]);
  const imageUrls = useMemo<string[]>(() => fileList.map((item) => item.url).filter(Boolean), [fileList]);
  const svgImageUrls = useSvgImageUrls(imageUrls);

  const isMaxCountReached = maxCount ? fileList.length >= maxCount : false;
  const previewBoxStyle: CSSProperties = fullWidth
    ? {
        width: '100%',
        aspectRatio: aspectRatio ? String(aspectRatio) : undefined,
        height: aspectRatio ? undefined : width,
      }
    : {
        width,
        height: width,
      };

  useEffect(() => {
    // 列表页「创建」会清空 currentData，UploadModal 仍挂载时 data 可能为 undefined
    const names = data?.imageNames || [];
    const urls = data?.imageUrls || [];
    form?.setFieldValue(fieldName, names);
    setFileList(
      urls.map((i, idx) => ({
        uid: i,
        name: names[idx],
        url: i,
      })),
    );
    setImageNames(names);
  }, [data, form, fieldName]);

  const onUpload = async (file: any) => {
    if (!data?.id) return;
    const key = buildImageKey(keyScope, data.id, file.name);
    const doUpload = tokenScope === 'dc' ? uploadImg : uploadImgGlobal;
    await doUpload({
      id: file.uid,
      form,
      file,
      key,
      module: 'cloud',
      onSuccess: () => {
        onUploadSuccess && onUploadSuccess(file);
        setImageNames((prev) => {
          const arr = [...new Set([...prev, file.name])];
          form?.setFieldValue(fieldName, arr);
          return arr;
        });
        if (save) {
          saveData();
        }
      },
      onProgress: ({ id, percent }) => {
        setFileList((prev) => {
          return prev.map((item) => {
            if (item.uid === id) {
              return {
                ...item,
                percent,
              };
            }
            return item;
          });
        });
      },
    });
  };

  const saveData = useCallback(async () => {
    try {
      if (!form || !onSaveImages || !data?.id) return;
      const values = await form.validate();
      await onSaveImages(data.id, values?.[fieldName]);
      onSaved?.();
    } catch (error) {
      console.error(error);
    }
  }, [data, form, fieldName, onSaveImages, onSaved]);

  const handleDelete = async (item) => {
    setFileList((prev) => prev.filter((f) => f.uid !== item.uid));
    const arr = imageNames.filter((name) => name !== item.name && name !== item.url);
    setImageNames(arr);

    if (form) {
      form.setFieldValue(fieldName, arr);
    }

    if (save) {
      await saveData();
      Message.success(t('common:delete.successful'));
    }
  };

  return (
    <Form.Item label={t('data-center:image')} noStyle={!showName}>
      <Form.Item field={fieldName} noStyle={!showName}>
        <div
          style={{
            display: 'flex',
            flexDirection: fullWidth ? 'column' : 'row',
            gap,
            flexWrap: 'wrap',
            width: fullWidth ? '100%' : undefined,
          }}
        >
          {fileList.map((item) => {
            return (
              <div
                className={`${styles.image_container} ${fullWidth ? styles.image_container_full : ''}`}
                key={item.uid}
                style={previewBoxStyle}
              >
                {item.percent < 100 && item.status === 'uploading' ? (
                  <Progress
                    style={{
                      ...previewBoxStyle,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    type="circle"
                    percent={item.percent}
                  />
                ) : (
                  <div
                    style={{
                      ...previewBoxStyle,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundImage: `url(${svgImageUrls[item.url] || item.url})`,
                      backgroundSize: fullWidth ? 'cover' : 'contain',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                    }}
                  >
                    <Image style={{ width: '100%', height: '100%', opacity: 0 }} src={svgImageUrls[item.url] || item.url} />
                  </div>
                )}
                {data?.status !== 'UP' && !disabled && (
                  <button
                    type="button"
                    className={styles.hover_mask}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item);
                    }}
                    aria-label="删除图片"
                  >
                    <IconDelete style={{ color: '#fff', fontSize: 14 }} />
                  </button>
                )}
              </div>
            );
          })}
          {data?.status !== 'UP' && !disabled && !isMaxCountReached && (
            <Upload
              fileList={fileList}
              showUploadList={false}
              beforeUpload={async (file) => {
                if (!beforeUploadValidate) return true;
                const origin = ((file as any)?.originFile || file) as File;
                const result = await beforeUploadValidate(origin);
                if (result === false) return false;
                if (result instanceof File) return result;
                return true;
              }}
              onChange={(currentFileList, currentFile) => {
                setFileList(currentFileList);
                if (currentFile.status === 'uploading' && currentFile.originFile) {
                  currentFile.url = URL.createObjectURL(currentFile.originFile);
                  onUpload(currentFile);
                }
              }}
              accept={accept}
              style={previewBoxStyle}
              disabled={disabled}
            >
              <div className="arco-upload-trigger-picture" style={previewBoxStyle}>
                <div className="arco-upload-trigger-picture-text">
                  <IconPlus />
                  <div style={{ marginTop: 10, fontWeight: 600 }}>{t('common:upload')}</div>
                </div>
              </div>
            </Upload>
          )}
        </div>
        {hint ? <div style={{ marginTop: 8, color: 'var(--color-text-3)', fontSize: 12, lineHeight: 1.6 }}>{hint}</div> : null}
      </Form.Item>
      {form && (
        <Form.Item field={fieldName} hidden>
          <Select mode="multiple" />
        </Form.Item>
      )}
    </Form.Item>
  );
}

/** 通用图片上传弹窗（产品类别等） */
export function UploadModal({
  visible,
  onCancel,
  onOk,
  record,
  keyScope = 'product-type',
  onSaveImages,
  maxCount,
  fullWidth,
  aspectRatio,
  hint,
  accept,
  beforeUploadValidate,
}: {
  visible: boolean;
  onCancel: () => void;
  onOk: (res?: any) => void;
  record: any;
  keyScope?: ImageKeyScope;
  onSaveImages: (id: string, imageNames: string[]) => Promise<any>;
  maxCount?: number;
  fullWidth?: boolean;
  aspectRatio?: number;
  hint?: ReactNode;
  accept?: string;
  beforeUploadValidate?: (file: File) => boolean | File | Promise<boolean | File>;
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  return (
    <Modal
      title={t('common:upload.img')}
      visible={visible}
      onCancel={onCancel}
      style={fullWidth ? { width: 720 } : undefined}
      onOk={async () => {
        try {
          if (!record?.id) return;
          const values = await form.validate();
          const res = await onSaveImages(record.id, values?.imageNames);
          onOk(res);
        } catch (error) {
          console.error(error);
        }
      }}
    >
      <Form form={form} layout="vertical">
        {record?.id ? (
          <ImageUploadItem
            form={form}
            data={record}
            keyScope={keyScope}
            onSaveImages={onSaveImages}
            maxCount={maxCount}
            fullWidth={fullWidth}
            aspectRatio={aspectRatio}
            hint={hint}
            accept={accept}
            beforeUploadValidate={beforeUploadValidate}
          />
        ) : null}
      </Form>
    </Modal>
  );
}
